import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { RequestTypes } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { SolicitudServicioDetail } from "@/services/service-requests.service";
import {
  closeFlightRequest,
  fetchFlightRequestDetail,
  markDoneFlightRequest,
  rejectFlightRequest,
} from "@/pages/ata-rsa-production/flight-requests/detail/handler-functions";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { SamplingRequestMainData } from "@/pages/ata-rsa-production/sampling-requests/components/SamplingRequestMainData";
import { ServiceRequestStatus } from "@/constants/entity-status";
import { FlightRequestActionButtons } from "./FlightRequestActionButtons";

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el.RejectRequestModal,
  })),
);
const MarkDoneSamplingRequestModalDynamic = lazy(() =>
  import("@/components/modal/MarkDoneSamplingRequestModal").then((el) => ({
    default: el.MarkDoneSamplingRequestModal,
  })),
);

export const FlightRequestsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions, hasWriteAccess } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState<SolicitudServicioDetail | null>(null);

  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();
  const [loadingReject, setLoadingReject] = useState(false);

  const { open: openDoneModal, setOpen: setOpenDoneModal } = useDisclosure();
  const [loadingDone, setLoadingDone] = useState(false);

  const [loadingClose, setLoadingClose] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const handleBack = () => {
    navigate(AppRoutes.FLIGHT_REQUESTS);
  };

  const fetchDetail = async (showLoad = true) =>
    fetchFlightRequestDetail({
      id,
      showLoad,
      setLoadingDetail,
      setDetail,
      navigate,
      fetchPermissions,
    });

  useEffect(() => {
    if (isActive && id) {
      fetchDetail();
    }
  }, [isActive, id]);

  const isRejected = detail?.estado === ServiceRequestStatus.REJECTED;
  const isDone = detail?.estado === ServiceRequestStatus.DONE;
  const isClosed = detail?.estado === ServiceRequestStatus.CLOSED;

  const handleRejectRequest = (event?: FieldValues) =>
    rejectFlightRequest({
      detailId: detail?.id ? Number(detail.id) : undefined,
      idParam: id,
      event,
      setLoadingReject,
      setOpenRejectModal,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const handleMarkDone = (values: {
    quantity: number;
    fechaRealizacion: string;
  }) =>
    markDoneFlightRequest({
      detailId: detail?.id ? Number(detail.id) : undefined,
      idParam: id,
      values,
      setLoadingDone,
      setOpenDoneModal,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const handleCloseRequest = () =>
    closeFlightRequest({
      detailId: detail?.id ? Number(detail.id) : undefined,
      idParam: id,
      setLoadingClose,
      navigate,
      fetchPermissions,
      fetchDetail,
    });

  const cantidadLabel = useMemo(() => "Hectáreas", []);
  const cantidadValue = useMemo(() => detail?.hectareas, [detail?.hectareas]);
  const doneModalQuantityLabel = useMemo(() => "hectáreas", []);
  const doneModalDefaultQuantity = useMemo(() => detail?.hectareas, [detail?.hectareas]);
  const showExecutionInfo = isDone || isClosed;
  const executedLabel = useMemo(() => "Hectáreas ejecutadas", []);
  const executedValue = useMemo(() => detail?.hectareasEjecutadas, [detail?.hectareasEjecutadas]);

  const executedFormatted = Number.isFinite(Number(executedValue))
    ? `${new Intl.NumberFormat("es-AR").format(Number(executedValue))}`
    : "-";

  const cantidadFormatted = Number.isFinite(Number(cantidadValue))
    ? `${new Intl.NumberFormat("es-AR").format(Number(cantidadValue))}`
    : "-";

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Solicitud de vuelo"
        onBack={handleBack}
        backButton
        loading={loadingDetail || loadingReject || loadingDone || loadingClose}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
          paddingRight="8px"
          paddingBottom="20px"
        >
          <FlightRequestActionButtons
            detail={detail}
            hasWriteAccess={hasWriteAccess}
            loadingReject={loadingReject}
            loadingDone={loadingDone}
            loadingClose={loadingClose}
            setOpenDoneModal={setOpenDoneModal}
            setOpenRejectModal={setOpenRejectModal}
            handleCloseRequest={handleCloseRequest}
          />

          {detail && (
            <SamplingRequestMainData
              detail={detail}
              cantidadLabel={cantidadLabel}
              cantidadFormatted={cantidadFormatted}
              executedLabel={executedLabel}
              executedFormatted={executedFormatted}
              showExecutionInfo={showExecutionInfo}
              isRejected={isRejected}
              isMuestrasUnidad={false}
            />
          )}

          <Suspense>
            <RejectRequestModalDynamic
              open={openRejectModal}
              setOpen={setOpenRejectModal}
              handleAccept={handleRejectRequest}
              loadingAccept={loadingReject}
              idTipoSolicitud={RequestTypes.Vuelo}
              entityName="solicitud"
            />
            <MarkDoneSamplingRequestModalDynamic
              open={openDoneModal}
              setOpen={setOpenDoneModal}
              quantityLabel={doneModalQuantityLabel}
              defaultQuantity={doneModalDefaultQuantity}
              allowDecimal
              handleAccept={handleMarkDone}
              loadingAccept={loadingDone}
              isMuestrasUnidad={false}
            />
          </Suspense>
        </Flex>
      )}
    </Flex>
  );
};